// src/slices/ticketDesignTemplate/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TICKET_DESIGN_TEMPLATE_STATUS,
  TicketDesignTemplate,
  TicketDesignTemplateFilters,
  TicketDesignTemplatePagination,
  TicketDesignTemplateModalState,
} from 'pages/TicketDesignTemplate/types';
import {
  fetchTicketDesignTemplates,
  fetchTicketDesignTemplateById,
  fetchTicketDesignTemplateFileUrl,
  createTicketDesignTemplate,
  updateTicketDesignTemplate,
  updateTicketDesignTemplateFile,
  updateTicketDesignTemplateLayout,
  deleteTicketDesignTemplateFile,
  deleteTicketDesignTemplate,
  approveTicketDesignTemplate,
  unlockTicketDesignTemplate,
} from './thunk';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template_messages');

interface TicketDesignTemplateState {
  templates: TicketDesignTemplate[];
  selectedTemplate: TicketDesignTemplate | null;
  fileUrl: string | null;          // Signed URL cho preview
  pagination: TicketDesignTemplatePagination;
  filters: TicketDesignTemplateFilters;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  loading: boolean;
  loadingDetail: boolean;
  loadingFileUrl: boolean;
  submitting: boolean;
  error: string | null;
  modalState: TicketDesignTemplateModalState;
}

const initialState: TicketDesignTemplateState = {
  templates: [],
  selectedTemplate: null,
  fileUrl: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
    status: 'All',
    designUnit: 'All',
    issuancePeriod: '',
  },
  sortBy: 'createdAt',
  sortOrder: 'desc',
  loading: false,
  loadingDetail: false,
  loadingFileUrl: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
    delete: false,
    approve: false,
    preview: false,
  },
};

const ticketDesignTemplateSlice = createSlice({
  name: 'TicketDesignTemplate',
  initialState,
  reducers: {
    setTemplates: (state, action: PayloadAction<TicketDesignTemplate[]>) => {
      state.templates = action.payload;
      state.loading = false;
    },
    setSelectedTemplate: (state, action: PayloadAction<TicketDesignTemplate | null>) => {
      state.selectedTemplate = action.payload;
    },
    clearFileUrl: (state) => {
      state.fileUrl = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<TicketDesignTemplatePagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action: PayloadAction<Partial<TicketDesignTemplateFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        searchTerm: '',
        status: 'All',
        designUnit: 'All',
        issuancePeriod: '',
      };
      state.pagination.currentPage = 1;
    },
    setSorting: (state, action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.field;
      state.sortOrder = action.payload.order;
    },
    openModal: (state, action: PayloadAction<keyof TicketDesignTemplateModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketDesignTemplateModalState>) => {
      state.modalState[action.payload] = false;
    },
    closeAllModals: (state) => {
      state.modalState = {
        create: false,
        edit: false,
        delete: false,
        approve: false,
        preview: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates list
      .addCase(fetchTicketDesignTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketDesignTemplates.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.templates = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems ?? payload.total ?? state.templates.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalItems / state.pagination.pageSize);
        state.pagination.currentPage = payload.currentPage ?? state.pagination.currentPage;
      })
      .addCase(fetchTicketDesignTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch template by ID
      .addCase(fetchTicketDesignTemplateById.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(fetchTicketDesignTemplateById.fulfilled, (state, action) => {
        state.selectedTemplate = action.payload;
        state.loadingDetail = false;
      })
      .addCase(fetchTicketDesignTemplateById.rejected, (state) => {
        state.loadingDetail = false;
      })

      // Fetch file URL (Signed URL)
      .addCase(fetchTicketDesignTemplateFileUrl.pending, (state) => {
        state.loadingFileUrl = true;
        state.fileUrl = null;
      })
      .addCase(fetchTicketDesignTemplateFileUrl.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.fileUrl = payload?.fileUrl || payload?.url || payload || null;
        state.loadingFileUrl = false;
      })
      .addCase(fetchTicketDesignTemplateFileUrl.rejected, (state) => {
        state.loadingFileUrl = false;
      })

      // Create template
      .addCase(createTicketDesignTemplate.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createTicketDesignTemplate.fulfilled, (state, action) => {
        state.submitting = false;
        state.templates.unshift(action.payload);
        state.pagination.totalItems += 1;
        state.modalState.create = false;
      })
      .addCase(createTicketDesignTemplate.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Update template
      .addCase(updateTicketDesignTemplate.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTicketDesignTemplate.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.templates.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        state.modalState.edit = false;
        state.selectedTemplate = null;
      })
      .addCase(updateTicketDesignTemplate.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Update template file
      .addCase(updateTicketDesignTemplateFile.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTicketDesignTemplateFile.fulfilled, (state, action) => {
        state.submitting = false;
        const template = action.payload as any;
        if (template?.id) {
          const index = state.templates.findIndex(t => t.id === template.id);
          if (index !== -1) {
            state.templates[index] = template;
          }
        }
      })
      .addCase(updateTicketDesignTemplateFile.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Lưu bố cục layout (mã vé, số vé, QR, ngày mở thưởng...) — chỉ phục vụ xem trước
      .addCase(updateTicketDesignTemplateLayout.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTicketDesignTemplateLayout.fulfilled, (state, action) => {
        state.submitting = false;
        const { id, layoutJson } = action.payload;
        const index = state.templates.findIndex(t => t.id === id);
        if (index !== -1) {
          state.templates[index] = { ...state.templates[index], layoutJson };
        }
        if (state.selectedTemplate?.id === id) {
          state.selectedTemplate = { ...state.selectedTemplate, layoutJson };
        }
      })
      .addCase(updateTicketDesignTemplateLayout.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Delete template file
      .addCase(deleteTicketDesignTemplateFile.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteTicketDesignTemplateFile.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.templates.findIndex(t => t.id === action.payload);
        if (index !== -1) {
          state.templates[index] = {
            ...state.templates[index],
            fileKey: null,
            fileName: null,
            fileSize: null,
            fileType: null,
            fileUrl: null,
          };
        }
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = {
            ...state.selectedTemplate,
            fileKey: null,
            fileName: null,
            fileSize: null,
            fileType: null,
            fileUrl: null,
          };
        }
        state.fileUrl = null;
      })
      .addCase(deleteTicketDesignTemplateFile.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Delete template
      .addCase(deleteTicketDesignTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTicketDesignTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.templates.findIndex(t => t.id === action.payload);
        if (index !== -1) {
          state.templates[index] = {
            ...state.templates[index],
            status: TICKET_DESIGN_TEMPLATE_STATUS.Locked,
            statusDisplayName: st('da_khoa_text'),
            isDeleted: true,
          };
        }
        state.modalState.delete = false;
        state.selectedTemplate = null;
      })
      .addCase(deleteTicketDesignTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Approve template
      .addCase(approveTicketDesignTemplate.pending, (state) => {
        state.submitting = true;
      })
      .addCase(approveTicketDesignTemplate.fulfilled, (state, action) => {
        state.submitting = false;
        const template = action.payload;
        const index = state.templates.findIndex(t => t.id === template.id);
        if (index !== -1) {
          state.templates[index] = template;
        }
        state.modalState.approve = false;
        state.selectedTemplate = null;
      })
      .addCase(approveTicketDesignTemplate.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Unlock template
      .addCase(unlockTicketDesignTemplate.pending, (state) => {
        state.submitting = true;
      })
      .addCase(unlockTicketDesignTemplate.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.templates.findIndex(t => t.id === action.payload);
        if (index !== -1) {
          state.templates[index] = {
            ...state.templates[index],
            status: TICKET_DESIGN_TEMPLATE_STATUS.Pending,
            statusDisplayName: st('cho_duyet_text'),
            isDeleted: false,
            approverId: null,
            approverName: null,
            approvedAt: null,
          };
        }
      })
      .addCase(unlockTicketDesignTemplate.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setTemplates,
  setSelectedTemplate,
  clearFileUrl,
  setLoading,
  setError,
  clearError,
  setPage,
  setPageSize,
  setPagination,
  setFilters,
  clearFilters,
  setSorting,
  openModal,
  closeModal,
  closeAllModals,
} = ticketDesignTemplateSlice.actions;

export default ticketDesignTemplateSlice.reducer;
